import React, { useRef, useState, useEffect, memo, useMemo, type ReactNode } from 'react';
import {
    Box, Stack, Typography, IconButton, Chip,
    FormControl, Select, MenuItem, SelectChangeEvent,
    Paper, Popper, ClickAwayListener, Tooltip, Button, CircularProgress,
    Checkbox, FormControlLabel, FormGroup, LinearProgress, TextField, Autocomplete,
    ToggleButton, ToggleButtonGroup, InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopy from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SyncIcon from '@mui/icons-material/Sync';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import { IOSSwitch } from '../../shared/Components/iosSwitch';
import { StyledIconButton } from '../../shared/Components/StyledIconButton';
import { SepiaChip } from '../../shared/Components/SepiaChip';
import { AutoFitText } from '../../shared/Components/AutoFitText';
import { TabPanel } from './TabComponents';
import { CopyEventDialog } from './CopyEventDialog';
import { AddPlayerDialog } from './AddPlayerDialog';
import { NewTournament } from './NewTournament';
import { EventDetailsSectionCard } from './Event Pages/EventDetailsSectionCard';
import { EventPublishStatusIcon } from './EventPublishStatusIcon';
import { eventListCol } from './eventListColumns';
import {
    getEventFillRate,
    getEventPublishIconKind,
    getEventStatusChipSx,
    getEventStatusLabel,
} from '../utils/eventCardStatus';
import { useTBContext } from '../Contexts/useTBStates';
import { useAuth } from 'react-oidc-context';
import { useOpenEvent } from '../hooks/useOpenEvent';
import CreateApi from '../../shared/Settings/appSettings';
import { setAppTheme } from '../../shared/Settings/appTheme';
import { formatDateForDisplay, isOpsActiveRegistration } from "@moment-golf/shared";
import type { EventGroup, EventGroupType, PlayerRoster, PlayerRosterEntry, CollectionStandings } from '@moment-golf/types';
import { SeriesPointsEditor } from './Point Systems/SeriesPointsEditor';
import type { TbEventWithId, CourseTeeSetData } from '@moment-golf/types';
import { DataGridPro, GridColDef, GridRenderCellParams, GridRenderEditCellParams, GRID_CHECKBOX_SELECTION_COL_DEF, useGridApiContext } from '@mui/x-data-grid-pro';
import { rosterFields, buildRosterColumns, resolveTeeSetOptions } from '../../shared/config/PlayerDataFields';
import { StandingsPreviewDialog, StandingsPreviewData } from './StandingsPreviewDialog';
import { CalculateThroughDialog } from './CalculateThroughDialog';

/** Autocomplete edit cell with freeSolo for use in DataGridPro row edit mode */
function FreeSoloEditCell({ options, appTheme, factor, ...params }: GridRenderEditCellParams & { options: string[]; appTheme: any; factor: number }) {
    const apiRef = useGridApiContext();
    const f = factor ?? 1;
    return (
        <Autocomplete
            freeSolo
            options={options}
            value={(params.value as string) ?? ''}
            onInputChange={(_, newValue) => {
                apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: newValue });
            }}
            renderInput={(inputProps) => (
                <TextField
                    {...inputProps}
                    variant='outlined'
                    sx={{
                        padding: 0,
                        '& .MuiInputBase-input': { fontSize: 12 * f, height: 12 * f, padding: 1 },
                        '& .MuiOutlinedInput-root': {
                            padding: '5px 10px',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: appTheme.palette.primary.main },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: appTheme.palette.primary.main },
                        },
                        '& input': { textAlign: 'left', lineHeight: 'normal' },
                    }}
                    inputProps={{ ...inputProps.inputProps, inputMode: 'text', autoComplete: 'off' }}
                />
            )}
            PopperComponent={(popperProps) => (
                <Popper {...popperProps} sx={{
                    zIndex: 1300,
                    '& .MuiAutocomplete-listbox': { maxHeight: 200, overflowY: 'auto', bgcolor: 'whitesmoke' },
                    '& .MuiAutocomplete-option': {
                        padding: '5px 10px',
                        fontSize: 12 * f,
                        '&:hover': { backgroundColor: `${appTheme.palette.primary.extraLight} !important`, borderRadius: 3 },
                    },
                    '& .Mui-focusVisible': { backgroundColor: `${appTheme.palette.primary.extraLight} !important`, borderRadius: 3 },
                }} />
            )}
            sx={{ width: '100%' }}
        />
    );
}

interface GroupEditorProps {
    appDims: any;
    compHeights: any;
    returnToList: () => void;
}

const theme = setAppTheme(1);

const GROUP_TYPES: EventGroupType[] = ['series', 'trip', 'league', 'other'];

function standingsGridSx(factor: number) {
    return {
        fontSize: 12 * factor,
        border: 'none',
        backgroundColor: 'sepia.background',
        '& .MuiDataGrid-main': { backgroundColor: 'sepia.background' },
        '& .MuiDataGrid-virtualScroller': { overscrollBehavior: 'none', backgroundColor: 'sepia.background' },
        '& .MuiDataGrid-columnHeader': { backgroundColor: 'sepia.background' },
        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, fontSize: 12 * factor },
    };
}

const standingsWrapCell = (params: GridRenderCellParams) => (
    <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35, py: 1 }}>
        {params.value}
    </Box>
);

function getOptions(optionsLoaded: unknown[], keyName: string): string[] {
    const entry = optionsLoaded?.find((o: any) => o?.keyName === keyName) as { options?: { group: string }[] } | undefined;
    return entry?.options?.map(o => o.group) ?? [];
}

type CollectionLinkedEvent = {
    _id: string;
    eventName: string;
    courseName: string;
    startDate: string;
    endDate: string;
    totalRounds: number;
    totalHoles: number;
    published: boolean;
    canceled?: boolean;
    playerCap?: number;
    playType?: string | null;
    privacyRegType?: string | null;
    eventCardImageURL?: string | null;
    registrationCount?: number;
};

function formatEventDateRange(event: { startDate: string; endDate: string }): string {
    const opts = { year: '2-digit' as const, month: 'numeric' as const, day: 'numeric' as const };
    if (new Date(event.startDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) === new Date(event.endDate).toLocaleDateString('en-US', { timeZone: 'UTC' })) {
        return formatDateForDisplay(event.startDate, opts);
    }
    return formatDateForDisplay(event.startDate, opts) + ' - ' + formatDateForDisplay(event.endDate, opts);
}

/** Matches tournament EventCard styling; keeps collection unlink/copy actions */
const CollectionEventCard = memo(({ event, factor, onOpen, onUnlink, onCopy }: {
    event: CollectionLinkedEvent;
    factor: number;
    onOpen: () => void;
    onUnlink: (e: React.MouseEvent) => void;
    onCopy: (e: React.MouseEvent) => void;
}) => {
    const [logoFailed, setLogoFailed] = useState(false);
    useEffect(() => {
        setLogoFailed(false);
    }, [event.eventCardImageURL]);

    const statusLabel = getEventStatusLabel(event);
    const publishKind = getEventPublishIconKind(event);
    const fillRate = getEventFillRate(event.playerCap, event.registrationCount);
    const registrationCount = event.registrationCount ?? 0;
    const playerCap = event.playerCap;
    const fillRateTooltip =
        playerCap != null && playerCap > 0
            ? `${Math.round(fillRate)}% filled — ${registrationCount} of ${playerCap} registered`
            : `${registrationCount} registered`;
    const logoSrc = !logoFailed && event.eventCardImageURL ? event.eventCardImageURL : null;

    return (
        <Box sx={{ width: 400 * factor, height: '100%' }}>
            <Paper
                elevation={0}
                onClick={onOpen}
                sx={{
                    padding: 1.5,
                    width: 400 * factor,
                    minHeight: 200 * factor,
                    borderRadius: '5px',
                    boxShadow: '0px 0px 4px 1px rgba(0,0,0,0.25)',
                    backgroundColor: 'background.paper',
                    position: 'relative',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    boxSizing: 'border-box',
                }}
            >
                <Stack direction='row' justifyContent='flex-end' spacing={0.5} sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}>
                    <IconButton size='small' onClick={onUnlink} aria-label='Unlink' title='Unlink from collection'>
                        <LinkOffIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' onClick={onCopy} aria-label='Copy event'>
                        <ContentCopy sx={{ fontSize: 18 }} />
                    </IconButton>
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            width: 100 * factor,
                            height: 80 * factor,
                            flexShrink: 0,
                            overflow: 'hidden',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {logoSrc && (
                            <Box
                                component="img"
                                src={logoSrc}
                                alt=""
                                onError={() => setLogoFailed(true)}
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    display: 'block',
                                }}
                            />
                        )}
                    </Box>
                    <Stack spacing={0.25} sx={{ flex: 1, flexShrink: 1, minWidth: 0, alignItems: 'flex-end', textAlign: 'right', pr: 5 }}>
                        <AutoFitText
                            variant="subtitle1"
                            maxFontSize={16 * factor}
                            minFontSize={10}
                            sx={{ fontWeight: 500, lineHeight: 1.2, textAlign: 'right' }}
                        >
                            {event.eventName}
                        </AutoFitText>
                        <AutoFitText
                            variant="body2"
                            maxFontSize={14 * factor}
                            minFontSize={9}
                            sx={{ lineHeight: 1.3, textAlign: 'right' }}
                        >
                            {event.courseName || ''}
                        </AutoFitText>
                        <Typography variant="body2" noWrap sx={{ width: '100%' }}>
                            Date(s) : {formatEventDateRange(event)}
                        </Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: '100%', px: 0.5 }}>
                    <Stack spacing={0.5} sx={{ width: 110 * factor }}>
                        <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', pr: 0.75 }}>Rounds</Typography>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left', pl: 0.75 }}>{event.totalRounds}</Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'right', pr: 0.75 }}>Holes</Typography>
                            <Typography variant="body2" sx={{ flex: 1, textAlign: 'left', pl: 0.75 }}>{event.totalHoles}</Typography>
                        </Stack>
                    </Stack>
                    <Stack spacing={0.75} sx={{ width: 80 * factor }}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ width: '100%', px: '10px', boxSizing: 'border-box' }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>Field</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'right' }}>{event.playerCap ?? '—'}</Typography>
                        </Stack>
                        <Tooltip title={fillRateTooltip} arrow placement="top">
                            <Box sx={{ width: '100%', display: 'flex' }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={fillRate}
                                    aria-label="Field fill rate"
                                    sx={{
                                        width: '100%',
                                        height: 10,
                                        borderRadius: 5,
                                        backgroundColor: 'sepia.extraLight',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 5,
                                            backgroundColor: 'sepia.main',
                                        },
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    </Stack>
                </Stack>

                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%', mt: 'auto', px: 0.5, gap: 1 }}
                >
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                        {event.playType && <SepiaChip label={event.playType} displayOnly />}
                        {statusLabel && <Chip label={statusLabel} size="small" sx={getEventStatusChipSx(statusLabel)} />}
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                        {event.privacyRegType && (
                            <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {event.privacyRegType}
                            </Typography>
                        )}
                        <EventPublishStatusIcon kind={publishKind} />
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
});

CollectionEventCard.displayName = 'CollectionEventCard';

/** Matches tournament EventListRow columns; keeps collection unlink/copy actions */
const CollectionEventRow = memo(({ event, appTheme, onOpen, onUnlink, onCopy }: {
    event: CollectionLinkedEvent;
    appTheme: any;
    onOpen: () => void;
    onUnlink: (e: React.MouseEvent) => void;
    onCopy: (e: React.MouseEvent) => void;
}) => {
    const statusLabel = getEventStatusLabel(event);
    const publishKind = getEventPublishIconKind(event);
    const fillRate = getEventFillRate(event.playerCap, event.registrationCount);
    const registrationCount = event.registrationCount ?? 0;
    const playerCap = event.playerCap;
    const fillRateTooltip =
        playerCap != null && playerCap > 0
            ? `${Math.round(fillRate)}% filled — ${registrationCount} of ${playerCap} registered`
            : `${registrationCount} registered`;

    return (
        <Stack
            direction='row'
            spacing={2}
            alignItems='center'
            onClick={onOpen}
            sx={{
                width: '100%',
                boxSizing: 'border-box',
                borderBottom: `1px solid ${appTheme.palette.primary.extraLight}`,
                padding: '8px 15px',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'primary.extraLight' },
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 500, ...eventListCol.eventName }}>
                {statusLabel && (
                    <Chip label={statusLabel} size="small" sx={getEventStatusChipSx(statusLabel, { fontSize: 10, mr: 1 })} />
                )}
                {event.eventName}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500, ...eventListCol.course }}>
                {event.courseName}
            </Typography>
            <Typography variant="caption" sx={eventListCol.dates}>{formatEventDateRange(event)}</Typography>
            <Typography variant="caption" sx={eventListCol.rounds}>{event.totalRounds}</Typography>
            <Typography variant="caption" sx={eventListCol.holes}>{event.totalHoles}</Typography>
            <Box sx={{ ...eventListCol.field, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Tooltip title={fillRateTooltip} arrow placement="top">
                    <Box sx={{ width: '100%', maxWidth: 56, display: 'flex' }}>
                        <LinearProgress
                            variant="determinate"
                            value={fillRate}
                            aria-label="Field fill rate"
                            sx={{
                                width: '100%',
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'sepia.extraLight',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: 'sepia.main',
                                },
                            }}
                        />
                    </Box>
                </Tooltip>
            </Box>
            <Typography variant="caption" sx={eventListCol.playType}>{event.playType || '—'}</Typography>
            <Typography variant="caption" sx={eventListCol.privacy}>{event.privacyRegType || '—'}</Typography>
            <Box sx={{ ...eventListCol.status, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <EventPublishStatusIcon kind={publishKind} fontSize={20} />
            </Box>
            <Stack direction='row' spacing={0} sx={{ flexShrink: 0, width: 72 }}>
                <IconButton size='small' onClick={onUnlink} aria-label='Unlink' title='Unlink from collection'>
                    <LinkOffIcon fontSize='small' />
                </IconButton>
                <IconButton size='small' onClick={onCopy} aria-label='Copy event'>
                    <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
            </Stack>
        </Stack>
    );
});

CollectionEventRow.displayName = 'CollectionEventRow';

export const GroupEditor = ({ appDims, compHeights, returnToList }: GroupEditorProps) => {

    const auth = useAuth();
    const api = CreateApi();

    const {
        factor, appTheme,
        viewGroup, setViewGroup,
        groupData, setGroupData,
        groupTabValue,
        editMode, setEditMode,
        optionsLoaded,
        eventData, setEventData,
        viewEventData,
        userData,
    } = useTBContext();

    const originalGroup = useRef<EventGroup | null>(viewGroup ? JSON.parse(JSON.stringify(viewGroup)) : null);
    const [saving, setSaving] = useState(false);

    // Standings tab state
    const [standingsView, setStandingsView] = useState<'individual' | 'pairs' | 'teams'>('individual');
    const [computingStandings, setComputingStandings] = useState(false);
    const [collectionStandings, setCollectionStandings] = useState<CollectionStandings | null>(
        viewGroup?.collectionStandings ?? null,
    );
    const [copiedEmbedUrl, setCopiedEmbedUrl] = useState(false);
    // Preview state
    const [previewingStandings, setPreviewingStandings] = useState(false);
    const [previewData, setPreviewData] = useState<StandingsPreviewData | null>(null);
    // Calculate-through picker
    const [calculateThroughOpen, setCalculateThroughOpen] = useState(false);
    const [eventsViewMode, setEventsViewMode] = useState<'cards' | 'list'>('cards');
    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copySourceEvent, setCopySourceEvent] = useState<TbEventWithId | null>(null);
    const [addEventOpen, setAddEventOpen] = useState(false);
    const addEventButtonRef = useRef<HTMLDivElement>(null);
    const [linkPopperOpen, setLinkPopperOpen] = useState(false);
    const linkPopperAnchorRef = useRef<HTMLButtonElement>(null);
    const DEFAULT_ROSTER_COLUMNS = ['playerName', 'playerEmail'];

    /**
     * Reads a raw playerRoster value from the DB (which may be the old array shape or the
     * new { template, players } shape) and normalises it to the new shape.
     */
    const normalizePlayerRoster = (raw: any): PlayerRoster => {
        if (Array.isArray(raw)) {
            // old shape — plain array of players, no template
            return { template: DEFAULT_ROSTER_COLUMNS, players: raw };
        }
        if (raw && typeof raw === 'object' && Array.isArray(raw.players)) {
            return raw as PlayerRoster;
        }
        return { template: DEFAULT_ROSTER_COLUMNS, players: [] };
    };

    const normalizeRosterPlayers = (players: Partial<PlayerRosterEntry>[]): PlayerRosterEntry[] =>
        players.map((p, i) => ({
            ...p,
            index: i,
            playerName: (p.playerName ?? '').trim(),
            userID: p.userID ?? null,
            playerEmail: (p.playerEmail ?? '').trim(),
        }));

    const initialRoster = normalizePlayerRoster(viewGroup?.playerRoster);
    const [playerRoster, setPlayerRoster] = useState<PlayerRosterEntry[]>(
        normalizeRosterPlayers(initialRoster.players),
    );
    const [syncingRoster, setSyncingRoster] = useState(false);
    const originalPlayerRoster = useRef<PlayerRosterEntry[]>(
        JSON.parse(JSON.stringify(normalizeRosterPlayers(initialRoster.players))),
    );
    const [allowSaveRoster, setAllowSaveRoster] = useState(false);

    // Roster column management — driven by playerRoster.template
    const [rosterColumns, setRosterColumns] = useState<string[]>(
        initialRoster.template.length ? initialRoster.template : DEFAULT_ROSTER_COLUMNS,
    );
    const [columnsPopperOpen, setColumnsPopperOpen] = useState(false);
    const columnsPopperAnchorRef = useRef<HTMLButtonElement>(null);
    const [addPlayerDialogOpen, setAddPlayerDialogOpen] = useState(false);

    useEffect(() => {
        const normalized = normalizePlayerRoster(viewGroup?.playerRoster);
        const roster = normalizeRosterPlayers(normalized.players);
        setPlayerRoster(roster);
        originalPlayerRoster.current = JSON.parse(JSON.stringify(roster));
    }, [viewGroup?._id, viewGroup?.playerRoster]);

    useEffect(() => {
        const normalized = normalizePlayerRoster(viewGroup?.playerRoster);
        if (normalized.template.length) {
            setRosterColumns(normalized.template);
        }
    }, [viewGroup?._id]);

    useEffect(() => {
        if (JSON.stringify(playerRoster) !== JSON.stringify(originalPlayerRoster.current)) {
            setAllowSaveRoster(true);
        } else {
            setAllowSaveRoster(false);
        }
    }, [playerRoster]);

    const rosterMetrics = useMemo(() => {
        const nameCounts = new Map<string, number>();
        const emailCounts = new Map<string, number>();
        for (const p of playerRoster) {
            const n = (p.playerName ?? '').trim().toLowerCase();
            if (n) nameCounts.set(n, (nameCounts.get(n) ?? 0) + 1);
            const e = (p.playerEmail ?? '').trim().toLowerCase();
            if (e) emailCounts.set(e, (emailCounts.get(e) ?? 0) + 1);
        }
        const duplicateNames = Array.from(nameCounts.values()).filter(c => c > 1).length;
        const duplicateEmails = Array.from(emailCounts.values()).filter(c => c > 1).length;
        return { duplicateNames, duplicateEmails };
    }, [playerRoster]);

    const openEvent = useOpenEvent();

    // Load user events (same as Tournament Manager / GetAllUserEvents) when GroupEditor mounts so Link Event has options
    useEffect(() => {
        const uid = auth.user?.profile?.sub;
        if (!uid) return;
        api.get('/tb/GetAllUserEvents', { params: { userID: uid, referenceDate: '1970-01-01T00:00:00.000Z' } })
            .then((res: { data: any[] }) => {
                if (Array.isArray(res.data)) setEventData(res.data);
            })
            .catch((err) => console.error('Error fetching events for group:', err));
    }, [auth.user?.profile?.sub, viewGroup?._id]);

    const userID = auth.user?.profile?.sub as string;
    const eventIds = viewGroup?.eventIDs ?? [];
    // Same data as Tournament Manager (GetAllUserEvents); don't filter by userID (not in API projection)
    const eventsNotInGroup = (eventData as any[]).filter((e: any) => !eventIds.includes(e._id));
    const linkedEvents = (eventData as any[]).filter((e: any) => eventIds.includes(e._id));

    const privacyGroupOptions = getOptions(optionsLoaded as unknown[], 'PrivacyGroup');
    const privacyRegTypeOptions = getOptions(optionsLoaded as unknown[], 'PrivacyRegType');
    const privacyViewTypeOptions = getOptions(optionsLoaded as unknown[], 'PrivacyViewType');
    const privacyAccessTypeOptions = getOptions(optionsLoaded as unknown[], 'PrivacyAccessType');

    if (!viewGroup) return null;

    // Resolve runtime options for the dynamic roster grid columns
    const linkedEventObjs = (eventData as any[]).filter((e: any) => (viewGroup.eventIDs ?? []).includes(e._id));
    const courseDataForGroup: CourseTeeSetData[] = (viewEventData as any)?.courseData ?? [];
    const teeSetOpts = resolveTeeSetOptions(courseDataForGroup);
    const divisionOpts: string[] = linkedEventObjs.flatMap((e: any) =>
        (e.flighting?.divisions ?? []).map((d: any) => d.divisionName ?? '').filter(Boolean)
    ).filter((v, i, a) => a.indexOf(v) === i);
    const flightOpts: string[] = linkedEventObjs.flatMap((e: any) =>
        (e.flighting?.flights ?? []).map((f: any) => f.flightName ?? '').filter(Boolean)
    ).filter((v, i, a) => a.indexOf(v) === i);

    // Merge event-sourced options with values already present in the roster so
    // freeSolo columns always suggest the full set of known values.
    const divisionAllOpts = Array.from(new Set([
        ...divisionOpts,
        ...playerRoster.map(p => (p as any).division).filter(Boolean),
    ]));
    const flightAllOpts = Array.from(new Set([
        ...flightOpts,
        ...playerRoster.map(p => (p as any).flight).filter(Boolean),
    ]));

    const baseRosterColumns = buildRosterColumns(rosterColumns, {
        teeSetOptions: teeSetOpts,
        divisionOptions: divisionAllOpts,
        flightOptions: flightAllOpts,
    });

    // Override division and flight columns to use freeSolo Autocomplete so users
    // can both pick from known values and type arbitrary new ones.
    const freeSoloOverrides: Record<string, Partial<GridColDef>> = {
        division: {
            type: 'string',
            renderEditCell: (params) => <FreeSoloEditCell {...params} options={divisionAllOpts} appTheme={appTheme} factor={factor} />,
        },
        flight: {
            type: 'string',
            renderEditCell: (params) => <FreeSoloEditCell {...params} options={flightAllOpts} appTheme={appTheme} factor={factor} />,
        },
    };

    const dynamicRosterColumns: GridColDef[] = [
        ...baseRosterColumns.map(col =>
            freeSoloOverrides[col.field]
                ? { ...col, ...freeSoloOverrides[col.field] } as GridColDef
                : col
        ),
        {
            field: 'userID',
            headerName: 'Linked Account',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            editable: false,
            renderCell: (params) => params.value
                ? <CheckCircleOutlinedIcon color='success' sx={{ fontSize: 18 }} />
                : null,
        } as GridColDef,
        {
            field: 'locked',
            headerName: 'Lock',
            width: 52,
            minWidth: 52,
            maxWidth: 52,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Tooltip title={params.row.locked ? 'Locked — Sync will not update' : 'Unlocked — Sync will update'}>
                    <Checkbox
                        size='small'
                        checked={!!params.row.locked}
                        onChange={() => handleToggleLock(params.row.index)}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Tooltip>
            ),
        } as GridColDef,
        {
            field: '__delete',
            headerName: '',
            width: 48,
            minWidth: 48,
            maxWidth: 48,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Tooltip title='Remove player'>
                    <IconButton
                        size='small'
                        color='error'
                        onClick={(e) => { e.stopPropagation(); handleDeleteRosterPlayer(params.row.index); }}
                        sx={{ padding: '2px' }}
                    >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            ),
        } as GridColDef,
    ];

    const updateField = (field: keyof EventGroup, value: any) => {
        setViewGroup({ ...viewGroup, [field]: value });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const updatedPlayerRoster: PlayerRoster = { template: rosterColumns, players: playerRoster };
            await api.post('/tb/UpdateGroup', {
                _id: viewGroup._id,
                userID: auth.user?.profile.sub,
                groupName: viewGroup.groupName,
                groupType: viewGroup.groupType,
                description: viewGroup.description,
                city: viewGroup.city ?? null,
                state: viewGroup.state ?? null,
                groupCardImageURL: viewGroup.groupCardImageURL ?? null,
                eventIDs: viewGroup.eventIDs ?? [],
                playerRoster: updatedPlayerRoster,
                published: viewGroup.published,
                active: viewGroup.active,
                privacyGroup: viewGroup.privacyGroup,
                privacyRegType: viewGroup.privacyRegType,
                privacyViewType: viewGroup.privacyViewType,
                privacyAccessType: viewGroup.privacyAccessType,
                seriesPoints: viewGroup.seriesPoints ?? null,
            });
            const updatedGroup = { ...viewGroup, playerRoster: updatedPlayerRoster };
            setViewGroup(updatedGroup);
            setGroupData(groupData.map(g => g._id === viewGroup._id ? updatedGroup : g));
            originalGroup.current = JSON.parse(JSON.stringify(updatedGroup));
            originalPlayerRoster.current = JSON.parse(JSON.stringify(playerRoster));
            setAllowSaveRoster(false);
            setEditMode(false);
        } catch (err) {
            console.error('Error saving group:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (originalGroup.current) {
            setViewGroup(JSON.parse(JSON.stringify(originalGroup.current)));
        }
        setEditMode(false);
    };

    // Delete UI removed for now; restore via returnToList + /tb/DeleteGroup when needed.
    void returnToList;

    const handleCopyEmails = async () => {
        const emails = Array.from(new Set(
            playerRoster
                .map(p => (p.playerEmail ?? '').trim().toLowerCase())
                .filter(Boolean)
        ));
        if (emails.length === 0) return;
        try {
            await navigator.clipboard.writeText(emails.join('\n'));
        } catch (err) {
            console.error('Failed to copy emails:', err);
        }
    };

    /**
     * Maps a registration record to roster fields, but only for keys that are
     * present in the current template (rosterColumns). Core identity fields
     * (playerName, playerEmail) are handled separately and never included here.
     */
    const extractFieldsFromReg = (reg: any): Partial<PlayerRosterEntry> => {
        const CORE_KEYS = new Set(['playerName', 'playerEmail', 'index', 'userID', 'locked']);
        const syncableKeys = rosterColumns.filter(k => !CORE_KEYS.has(k));
        const result: Partial<PlayerRosterEntry> = {};
        for (const key of syncableKeys) {
            // playerHandicap has a legacy alias 'handicap' on some registration docs
            if (key === 'playerHandicap') {
                const val = reg.playerHandicap ?? reg.handicap;
                if (val != null) result.playerHandicap = val;
            // division may be stored as 'divisionName' on registrations created via flighting assignment
            } else if (key === 'division') {
                const val = reg.division ?? reg.divisionName;
                if (val != null && val !== '') result.division = val;
            } else if (reg[key] != null && reg[key] !== '') {
                (result as any)[key] = reg[key];
            }
        }
        return result;
    };

    const handleSyncRoster = async () => {
        if (!viewGroup.eventIDs?.length) return;
        setSyncingRoster(true);
        try {
            const results = await Promise.all(
                viewGroup.eventIDs.map((id: string) =>
                    api.get('/tb/GetEventRegistrations', { params: { _id: id } }).then((r: any) => r.data)
                )
            );
            const allRegs: any[] = results.flat();

            // Build a map: playerKey → merged registration across all events.
            // Later registrations fill in any fields that are null/empty in the first,
            // so division (or any other field) is found even if it only exists in one event.
            // Skip refunded/WD/DQ/canceled without an EventScore (played-then-stopped keep score).
            const regByKey = new Map<string, any>();
            for (const reg of allRegs) {
                if (!isOpsActiveRegistration(reg)) continue;
                const name = (reg.playerName ?? '').trim();
                const email = (reg.playerEmail ?? '').trim();
                if (!name && !email) continue;
                const key = `${name.toLowerCase()}|${email.toLowerCase()}`;
                if (!regByKey.has(key)) {
                    regByKey.set(key, { ...reg });
                } else {
                    const existing = regByKey.get(key);
                    for (const field of Object.keys(reg)) {
                        if (existing[field] == null || existing[field] === '') {
                            existing[field] = reg[field];
                        }
                    }
                }
            }

            // Update existing roster entries: overwrite all non-core fields from registration for unlocked rows.
            // Also pick up userID from the registration when the roster entry doesn't have one yet,
            // so the Linked Account column stays current after SyncRegistrationUsers runs on the event.
            const merged = playerRoster.map(entry => {
                if (entry.locked) return entry;
                const key = `${entry.playerName.toLowerCase()}|${entry.playerEmail.toLowerCase()}`;
                const reg = regByKey.get(key);
                if (!reg) return entry;
                const regFields = extractFieldsFromReg(reg);
                const userID = entry.userID ?? reg.userID ?? null;
                return { ...entry, ...regFields, userID } as PlayerRosterEntry;
            });

            // Add new entries for players not yet on the roster
            const existingKeys = new Set(merged.map(p => `${p.playerName.toLowerCase()}|${p.playerEmail.toLowerCase()}`));
            const newEntries: PlayerRosterEntry[] = [];
            for (const [key, reg] of Array.from(regByKey.entries())) {
                if (existingKeys.has(key)) continue;
                const name = (reg.playerName ?? '').trim();
                const email = (reg.playerEmail ?? '').trim();
                newEntries.push({
                    ...extractFieldsFromReg(reg),
                    index: merged.length + newEntries.length,
                    playerName: name,
                    userID: reg.userID ?? null,
                    playerEmail: email,
                });
            }

            if (newEntries.length === 0 && JSON.stringify(merged) === JSON.stringify(playerRoster)) return;

            const updatedRoster = [...merged, ...newEntries];
            const updatedPlayerRoster: PlayerRoster = { template: rosterColumns, players: updatedRoster };
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: auth.user?.profile.sub,
                playerRoster: updatedPlayerRoster,
            });
            setPlayerRoster(updatedRoster);
            setViewGroup({ ...viewGroup, playerRoster: updatedPlayerRoster });
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? { ...g, playerRoster: updatedPlayerRoster } : g));
            originalPlayerRoster.current = JSON.parse(JSON.stringify(updatedRoster));
            setAllowSaveRoster(false);
        } catch (err) {
            console.error('Error syncing roster:', err);
        } finally {
            setSyncingRoster(false);
        }
    };

    const handleDeleteRosterPlayer = (rowIndex: number) => {
        const updated = playerRoster
            .filter(p => p.index !== rowIndex)
            .map((p, i) => ({ ...p, index: i }));
        setPlayerRoster(updated);
    };

    const handleToggleLock = (rowIndex: number) => {
        const idx = playerRoster.findIndex(p => p.index === rowIndex);
        if (idx === -1) return;
        const updated = [...playerRoster];
        updated[idx] = { ...updated[idx], locked: !updated[idx].locked };
        setPlayerRoster(updated);
    };

    const handleLockUnlockAll = () => {
        const allLocked = playerRoster.length > 0 && playerRoster.every(p => p.locked);
        setPlayerRoster(playerRoster.map(p => ({ ...p, locked: !allLocked })));
    };

    const handleAddPlayer = async (entry: Omit<PlayerRosterEntry, 'index'>) => {
        const fullEntry: PlayerRosterEntry = {
            ...entry,
            index: playerRoster.length,
        } as PlayerRosterEntry;
        const updatedPlayers = [...playerRoster, fullEntry];
        setPlayerRoster(updatedPlayers);
        try {
            const updatedPlayerRoster: PlayerRoster = { template: rosterColumns, players: updatedPlayers };
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: auth.user?.profile.sub,
                playerRoster: updatedPlayerRoster,
            });
            const updatedGroup = { ...viewGroup, playerRoster: updatedPlayerRoster };
            setViewGroup(updatedGroup);
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? updatedGroup : g));
            originalPlayerRoster.current = JSON.parse(JSON.stringify(updatedPlayers));
            setAllowSaveRoster(false);
        } catch (err) {
            console.error('Error saving roster after adding player:', err);
        }
    };

    const handleSaveRoster = async () => {
        setSaving(true);
        try {
            const updatedPlayerRoster: PlayerRoster = { template: rosterColumns, players: playerRoster };
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: auth.user?.profile.sub,
                playerRoster: updatedPlayerRoster,
            });
            const updatedGroup = { ...viewGroup, playerRoster: updatedPlayerRoster };
            setViewGroup(updatedGroup);
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? updatedGroup : g));
            originalPlayerRoster.current = JSON.parse(JSON.stringify(playerRoster));
            setAllowSaveRoster(false);
        } catch (err) {
            console.error('Error saving roster:', err);
        } finally {
            setSaving(false);
        }
    };

    /** Open the calculate-through event picker dialog */
    const handleComputeStandings = () => {
        if (!viewGroup?._id) return;
        setCalculateThroughOpen(true);
    };

    /** Called by CalculateThroughDialog when the user confirms a target event */
    const handleConfirmCalculate = async (targetEventID: string) => {
        setCalculateThroughOpen(false);
        if (!viewGroup?._id) return;
        setComputingStandings(true);
        try {
            const result = await api.post('/tb/ComputeCollectionStandings', {
                groupID: viewGroup._id,
                targetEventID,
            });
            const standings: CollectionStandings = result.data.collectionStandings;
            setCollectionStandings(standings);
            const updatedGroup = { ...viewGroup, collectionStandings: standings };
            setViewGroup(updatedGroup);
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? updatedGroup : g));
        } catch (err) {
            console.error('Error computing collection standings:', err);
        } finally {
            setComputingStandings(false);
        }
    };

    const handlePreviewStandings = async () => {
        if (!viewGroup?._id) return;
        setPreviewingStandings(true);
        try {
            const result = await api.post('/tb/PreviewCollectionStandings', { groupID: viewGroup._id });
            setPreviewData({
                referenceStandings: result.data.referenceStandings ?? null,
                activeEventSummaries: result.data.activeEventSummaries ?? [],
                projectedStandings: result.data.projectedStandings,
            });
        } catch (err) {
            console.error('Error previewing collection standings:', err);
        } finally {
            setPreviewingStandings(false);
        }
    };

    const handleToggleRosterColumn = async (key: string, checked: boolean) => {
        const LOCKED_KEYS = ['playerName', 'playerEmail'];
        if (LOCKED_KEYS.includes(key)) return;
        const next = checked
            ? [...rosterColumns, key]
            : rosterColumns.filter(k => k !== key);
        setRosterColumns(next);
        // Persist immediately so the template survives a page reload
        try {
            const updatedPlayerRoster: PlayerRoster = { template: next, players: playerRoster };
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: auth.user?.profile.sub,
                playerRoster: updatedPlayerRoster,
            });
            const updated = { ...viewGroup, playerRoster: updatedPlayerRoster };
            setViewGroup(updated);
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? updated : g));
        } catch (err) {
            console.error('Error saving roster columns:', err);
        }
    };

    const linkEvent = async (selectedEvent: any) => {
        if (!selectedEvent || viewGroup.eventIDs?.includes(selectedEvent._id)) return;
        try {
            const currentIds = Array.isArray(selectedEvent.eventGroupIDs)
                ? selectedEvent.eventGroupIDs
                : (selectedEvent.eventGroupID ? [selectedEvent.eventGroupID] : []);
            const nextGroupIds = currentIds.includes(viewGroup._id!) ? currentIds : [...currentIds, viewGroup._id!];
            await api.post('/tb/UpdateEvent', {
                _id: selectedEvent._id,
                eventGroupIDs: nextGroupIds,
                userID: userID,
            });
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: userID,
                eventIDs: [...(viewGroup.eventIDs || []), selectedEvent._id],
            });
            const nextIds = [...(viewGroup.eventIDs || []), selectedEvent._id];
            setViewGroup({ ...viewGroup, eventIDs: nextIds });
            setGroupData(groupData.map(g => g._id === viewGroup._id ? { ...g, eventIDs: nextIds } : g));
            setEventData((prev: any[]) => prev.map(e => e._id === selectedEvent._id ? { ...e, eventGroupIDs: nextGroupIds } : e));
        } catch (err) {
            console.error('Error linking event:', err);
        }
    };

    const unlinkEvent = async (event: any, e?: React.MouseEvent) => {
        e?.stopPropagation();
        try {
            const currentIds = Array.isArray(event.eventGroupIDs)
                ? event.eventGroupIDs
                : (event.eventGroupID ? [event.eventGroupID] : []);
            const nextGroupIds = currentIds.filter((id: string) => id !== viewGroup._id);
            await api.post('/tb/UpdateEvent', {
                _id: event._id,
                eventGroupIDs: nextGroupIds,
                userID: userID,
            });
            const nextIds = (viewGroup.eventIDs || []).filter((id: string) => id !== event._id);
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: userID,
                eventIDs: nextIds,
            });
            setViewGroup({ ...viewGroup, eventIDs: nextIds });
            setGroupData(groupData.map(g => g._id === viewGroup._id ? { ...g, eventIDs: nextIds } : g));
            setEventData((prev: any[]) => prev.map(ev => ev._id === event._id ? { ...ev, eventGroupIDs: nextGroupIds } : ev));
        } catch (err) {
            console.error('Error unlinking event:', err);
        }
    };

    const handleOpenCopyDialog = async (event: TbEventWithId) => {
        try {
            const res = await api.get<TbEventWithId>('/tb/GetEventDetails', {
                params: { _id: event._id, referenceDate: '1970-01-01' },
            });
            if (res.data) {
                setCopySourceEvent(res.data);
                setCopyDialogOpen(true);
            } else {
                window.alert('Could not load event details. Please try again.');
            }
        } catch (err) {
            console.error('Error fetching event for copy:', err);
            window.alert('Could not load event. Please try again.');
        }
    };

    const handleCopyEventCreated = async (newEvent: TbEventWithId) => {
        if (!newEvent._id) return;
        const nextIds = [...(viewGroup.eventIDs || []), newEvent._id];
        const groupIds = Array.from(new Set([...(newEvent.eventGroupIDs || (newEvent.eventGroupID ? [newEvent.eventGroupID] : [])), viewGroup._id!]));
        setViewGroup({ ...viewGroup, eventIDs: nextIds });
        setEventData((prev: any[]) => [...prev, { ...newEvent, eventGroupIDs: groupIds }]);
        try {
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: userID,
                eventIDs: nextIds,
            });
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? { ...g, eventIDs: nextIds } : g));
        } catch (err) {
            console.error('Error persisting group eventIDs:', err);
        }
    };

    const handleNewEventCreated = async (newEvent: any) => {
        if (!newEvent?._id) return;
        const nextIds = [...(viewGroup.eventIDs || []), newEvent._id];
        const groupIds = Array.from(new Set([...(newEvent.eventGroupIDs || (newEvent.eventGroupID ? [newEvent.eventGroupID] : [])), viewGroup._id!]));
        setViewGroup({ ...viewGroup, eventIDs: nextIds });
        setEventData((prev: any[]) => [...prev, { ...newEvent, eventGroupIDs: groupIds }]);
        try {
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: userID,
                eventIDs: nextIds,
            });
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? { ...g, eventIDs: nextIds } : g));
        } catch (err) {
            console.error('Error persisting group eventIDs:', err);
        }
    };

    const handleCleanseEventIDs = async () => {
        const currentIds = viewGroup.eventIDs ?? [];
        const validIdsSet = new Set(linkedEvents.map((e: any) => String(e._id)));
        const cleansedIds = currentIds.filter((id: string) => validIdsSet.has(String(id)));
        if (cleansedIds.length === currentIds.length) return;
        try {
            await api.post('/tb/UpdateGroup', {
                ...viewGroup,
                _id: viewGroup._id,
                userID: userID,
                eventIDs: cleansedIds,
            });
            setViewGroup({ ...viewGroup, eventIDs: cleansedIds });
            setGroupData(groupData.map((g: any) => g._id === viewGroup._id ? { ...g, eventIDs: cleansedIds } : g));
        } catch (err) {
            console.error('Error cleansing group eventIDs:', err);
        }
    };

    const showCleanseButton = (viewGroup.eventIDs?.length ?? 0) !== linkedEvents.length;

    const ownerDisplayName =
        (viewGroup as any).ownerName
        ?? (viewGroup.ownerID === auth.user?.profile?.sub ? (userData as { displayName?: string })?.displayName : null)
        ?? viewGroup.ownerID;

    const detailsColumnCount = appDims?.appWidth > 1200 ? 3 : appDims?.appWidth > 750 ? 2 : 1;
    const rowMinHeight = 32 * factor;

    const groupFieldRow = (label: string, value: ReactNode) => (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                minHeight: rowMinHeight,
                boxSizing: 'border-box',
                justifyContent: 'flex-start',
            }}
        >
            <Typography
                flex={2}
                textAlign="left"
                sx={{
                    fontWeight: 500,
                    fontSize: 14 * factor,
                    lineHeight: 1.4,
                    minHeight: rowMinHeight,
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flexShrink: 1,
                }}
            >
                {label}:
            </Typography>
            <Box
                flex={3}
                sx={{
                    minWidth: 0,
                    minHeight: rowMinHeight,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                {value}
            </Box>
        </Stack>
    );

    const groupTextField = (
        value: string,
        onChange: (v: string) => void,
        placeholder?: string,
    ) => (
        <TextField
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            size="small"
            fullWidth
            sx={{
                '& .MuiInputBase-input': {
                    fontSize: 14 * factor,
                    height: rowMinHeight - 2,
                    padding: '0px 8px',
                    boxSizing: 'border-box',
                },
                '& .MuiOutlinedInput-root': {
                    minHeight: rowMinHeight,
                    height: rowMinHeight,
                },
            }}
        />
    );

    const groupSelectField = (
        value: string,
        onChange: (v: string) => void,
        options: string[],
        opts?: { capitalizeOptions?: boolean; allowEmpty?: boolean },
    ) => (
        <FormControl size="small" fullWidth>
            <Select
                value={value}
                onChange={(e: SelectChangeEvent) => onChange(e.target.value)}
                sx={{
                    fontSize: 14 * factor,
                    height: rowMinHeight,
                    '& .MuiSelect-select': {
                        py: 0,
                        display: 'flex',
                        alignItems: 'center',
                        ...(opts?.capitalizeOptions ? { textTransform: 'capitalize' } : {}),
                    },
                }}
            >
                {opts?.allowEmpty !== false && <MenuItem value="">— None —</MenuItem>}
                {options.map((opt) => (
                    <MenuItem key={opt} value={opt} sx={opts?.capitalizeOptions ? { textTransform: 'capitalize' } : undefined}>
                        {opt}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const detailsSectionCards: ReactNode[] = [
        <EventDetailsSectionCard key="core-items" title="Core Items">
            {groupFieldRow(
                'Owner',
                <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>{ownerDisplayName}</Typography>,
            )}
            {groupFieldRow(
                'Type',
                editMode ? (
                    groupSelectField(
                        viewGroup.groupType,
                        (v) => updateField('groupType', v as EventGroupType),
                        GROUP_TYPES,
                        { capitalizeOptions: true, allowEmpty: false },
                    )
                ) : (
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500, textTransform: 'capitalize' }}>
                        {viewGroup.groupType}
                    </Typography>
                ),
            )}
            {groupFieldRow(
                'Events',
                <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>
                    {viewGroup.eventIDs?.length ?? 0}
                </Typography>,
            )}
            {groupFieldRow(
                'Published',
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>No</Typography>
                    <IOSSwitch
                        checked={viewGroup.published ?? false}
                        disabled={!editMode}
                        onChange={(e: any) => updateField('published', e.target.checked)}
                    />
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>Yes</Typography>
                </Stack>,
            )}
        </EventDetailsSectionCard>,
        <EventDetailsSectionCard key="collection-information" title="Collection Information">
            {groupFieldRow(
                'Collection Name',
                editMode
                    ? groupTextField(viewGroup.groupName, (v) => updateField('groupName', v), 'Collection Name')
                    : <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>{viewGroup.groupName || 'Collection Name'}</Typography>,
            )}
            {groupFieldRow(
                'Description',
                editMode
                    ? groupTextField(viewGroup.description ?? '', (v) => updateField('description', v || null), 'Description')
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.description ? 'inherit' : 'text.disabled'}>
                        {viewGroup.description || 'Description'}
                    </Typography>,
            )}
            {groupFieldRow(
                'City',
                editMode
                    ? groupTextField(viewGroup.city ?? '', (v) => updateField('city', v || null), 'City')
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.city ? 'inherit' : 'text.disabled'}>
                        {viewGroup.city || 'City'}
                    </Typography>,
            )}
            {groupFieldRow(
                'State',
                editMode
                    ? groupTextField(viewGroup.state ?? '', (v) => updateField('state', v || null), 'State')
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.state ? 'inherit' : 'text.disabled'}>
                        {viewGroup.state || 'State'}
                    </Typography>,
            )}
        </EventDetailsSectionCard>,
        <EventDetailsSectionCard key="trip-card-image" title="Trip Card Image">
            {editMode ? (
                <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Stack
                        spacing={1}
                        sx={{
                            width: 200,
                            height: 120,
                            padding: 2,
                            border: `1px solid ${theme.palette.primary.light}`,
                            borderRadius: 1,
                            backgroundColor: 'background.paper',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                            e.preventDefault();
                            const file = e.dataTransfer.files[0];
                            if (file && file.type.startsWith('image/') && viewGroup?._id) {
                                const formData = new FormData();
                                formData.append('image', file, file.name);
                                formData.append('_id', viewGroup._id);
                                formData.append('userID', auth.user?.profile?.sub || '');
                                api.post('/image/uploadEventLogo', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                                    .then((res) => {
                                        if (res.status === 200 && res.data.imageUrl) {
                                            api.post('/tb/saveGroupImage', { _id: viewGroup._id, imageUrl: res.data.imageUrl }).then(() => {
                                                setViewGroup((g) => g ? { ...g, groupCardImageURL: res.data.imageUrl } : null);
                                                setGroupData((prev) => prev.map((g) => g._id === viewGroup._id ? { ...g, groupCardImageURL: res.data.imageUrl } : g));
                                            });
                                        }
                                    })
                                    .catch((err) => console.error('Error uploading group image:', err));
                            }
                        }}
                    >
                        <Typography variant="body2" sx={{ textAlign: 'center' }}>Drag and drop image</Typography>
                        <Box
                            component="img"
                            src={viewGroup.groupCardImageURL || undefined}
                            alt="Trip preview"
                            sx={{
                                maxWidth: '100%',
                                maxHeight: 80,
                                objectFit: 'contain',
                                display: viewGroup.groupCardImageURL ? 'block' : 'none',
                            }}
                        />
                    </Stack>
                    <Stack direction="column" spacing={1}>
                        <Button variant="outlined" size="small" component="label" sx={{ width: 'max-content' }}>
                            Choose File
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file && viewGroup?._id) {
                                        const formData = new FormData();
                                        formData.append('image', file, file.name);
                                        formData.append('_id', viewGroup._id);
                                        formData.append('userID', auth.user?.profile?.sub || '');
                                        api.post('/image/uploadEventLogo', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
                                            .then((res) => {
                                                if (res.status === 200 && res.data.imageUrl) {
                                                    return api.post('/tb/saveGroupImage', { _id: viewGroup._id, imageUrl: res.data.imageUrl }).then(() => {
                                                        setViewGroup((g) => g ? { ...g, groupCardImageURL: res.data.imageUrl } : null);
                                                        setGroupData((prev) => prev.map((g) => g._id === viewGroup._id ? { ...g, groupCardImageURL: res.data.imageUrl } : g));
                                                    });
                                                }
                                            })
                                            .catch((err) => console.error('Error uploading group image:', err));
                                    }
                                    e.target.value = '';
                                }}
                            />
                        </Button>
                        {viewGroup.groupCardImageURL && (
                            <Button
                                variant="outlined"
                                size="small"
                                color="error"
                                onClick={() => {
                                    if (viewGroup._id) {
                                        api.post('/tb/UpdateGroup', { _id: viewGroup._id, userID: auth.user?.profile?.sub, groupCardImageURL: null }).then(() => {
                                            setViewGroup((g) => g ? { ...g, groupCardImageURL: null } : null);
                                            setGroupData((prev) => prev.map((g) => g._id === viewGroup._id ? { ...g, groupCardImageURL: null } : g));
                                        });
                                    }
                                }}
                            >
                                Remove
                            </Button>
                        )}
                    </Stack>
                </Stack>
            ) : viewGroup.groupCardImageURL ? (
                <Box
                    component="img"
                    src={viewGroup.groupCardImageURL}
                    alt="Trip preview"
                    sx={{ maxWidth: 200, maxHeight: 120, objectFit: 'contain', borderRadius: 1 }}
                />
            ) : (
                <Typography sx={{ fontSize: 14 * factor, color: 'text.disabled' }}>No image</Typography>
            )}
        </EventDetailsSectionCard>,
        <EventDetailsSectionCard key="access-visibility" title="Access & Visibility">
            {groupFieldRow(
                'Privacy Group',
                editMode
                    ? groupSelectField(viewGroup.privacyGroup ?? '', (v) => updateField('privacyGroup', v || null), privacyGroupOptions)
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.privacyGroup ? 'inherit' : 'text.disabled'}>
                        {viewGroup.privacyGroup || 'Privacy Group'}
                    </Typography>,
            )}
            {groupFieldRow(
                'Registration Privacy',
                editMode
                    ? groupSelectField(viewGroup.privacyRegType ?? '', (v) => updateField('privacyRegType', v || null), privacyRegTypeOptions)
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.privacyRegType ? 'inherit' : 'text.disabled'}>
                        {viewGroup.privacyRegType || 'Registration Privacy'}
                    </Typography>,
            )}
            {groupFieldRow(
                'Viewing Privacy',
                editMode
                    ? groupSelectField(viewGroup.privacyViewType ?? '', (v) => updateField('privacyViewType', v || null), privacyViewTypeOptions)
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.privacyViewType ? 'inherit' : 'text.disabled'}>
                        {viewGroup.privacyViewType || 'Viewing Privacy'}
                    </Typography>,
            )}
            {groupFieldRow(
                'Access Method',
                editMode
                    ? groupSelectField(viewGroup.privacyAccessType ?? '', (v) => updateField('privacyAccessType', v || null), privacyAccessTypeOptions)
                    : <Typography sx={{ fontSize: 14 * factor }} color={viewGroup.privacyAccessType ? 'inherit' : 'text.disabled'}>
                        {viewGroup.privacyAccessType || 'Access Method'}
                    </Typography>,
            )}
        </EventDetailsSectionCard>,
    ];

    const detailsColumns: ReactNode[][] = Array.from({ length: detailsColumnCount }, () => []);
    detailsSectionCards.forEach((card, index) => {
        detailsColumns[index % detailsColumnCount].push(card);
    });

    return (
        <>
        <Stack id='group-view-container' direction={'column'} spacing={0}
            sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>

            <Stack id='group-view-header'
                direction={'row'} spacing={0.5}
                sx={{
                    width: '100%',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    padding: '10px 20px',
                    backgroundColor: 'sepia.background',
                    borderBottom: `1px solid ${appTheme.palette.primary.main}`,
                    boxSizing: 'border-box',
                }}
            >
                <Stack id='group-view-main-header'
                    direction={'row'} spacing={3}
                    sx={{
                        width: '100%',
                        padding: '0px 10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        minHeight: 35,
                        alignItems: 'center',
                    }}
                >
                    <Stack direction={'row'} spacing={1.5} sx={{ flex: 1, alignItems: 'center', justifyContent: 'flex-start' }}>
                        <Typography variant='h6' sx={{ fontWeight: 500 }}>{viewGroup.groupName}</Typography>
                        <Chip
                            label={viewGroup.groupType}
                            size='small'
                            color='primary'
                            sx={{ textTransform: 'capitalize' }}
                        />
                    </Stack>

                    <Stack direction={'row'} spacing={1} sx={{ ml: 'auto', alignItems: 'center', flexShrink: 0 }}>
                        {!editMode ? (
                            <Button
                                variant='outlined'
                                color='primary'
                                startIcon={<EditIcon fontSize="small" />}
                                sx={{ height: 30 * factor }}
                                onClick={() => setEditMode(true)}
                            >
                                Edit
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant='outlined'
                                    color='primary'
                                    sx={{ width: 100 * factor, height: 30 * factor }}
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    variant='outlined'
                                    color='error'
                                    sx={{ width: 100 * factor, height: 30 * factor }}
                                    onClick={handleCancel}
                                >
                                    Close
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Stack>

            <Stack id='group-view-body' direction={'row'} spacing={0}
                sx={{ padding: '0px 0px', width: '100%', flex: 1, minHeight: 0, alignItems: 'stretch' }}>

                <Stack id='group-view-main' direction={'column'} sx={{ width: '100%', height: '100%', flex: 1, minHeight: 0 }}>
                    <Stack id='group-view-main-body' direction={'column'} sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

                            {/* Tab 0 — Events */}
                            <TabPanel value={groupTabValue} index={0} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
                                <Stack direction={'column'} spacing={1} sx={{ height: '100%', minHeight: 0, overflow: 'hidden', p: 1.5, boxSizing: 'border-box' }}>
                                    <Typography variant='subtitle1' color='primary' sx={{ fontWeight: 600, borderBottom: `1px dashed ${theme.palette.divider}`, pb: 0.5, flexShrink: 0 }}>
                                        Linked Events
                                    </Typography>
                                    <Stack direction={'row'} spacing={2} alignItems={'center'} flexWrap='wrap' sx={{ gap: 1, flexShrink: 0 }}>
                                        <Tooltip title='Link events'>
                                            <IconButton
                                                ref={linkPopperAnchorRef}
                                                size='small'
                                                color='primary'
                                                onClick={() => setLinkPopperOpen(prev => !prev)}
                                                aria-label='Link events to collection'
                                            >
                                                <LinkIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Popper
                                            open={linkPopperOpen}
                                            anchorEl={linkPopperAnchorRef.current}
                                            placement='bottom-start'
                                            disablePortal={false}
                                            modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                                            sx={{ zIndex: theme.zIndex.tooltip }}
                                        >
                                            <ClickAwayListener onClickAway={() => setLinkPopperOpen(false)}>
                                                <Paper elevation={4} sx={{ p: 2, minWidth: 320 }}>
                                                    <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 0.5 }}>Link Events</Typography>
                                                    <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                                                        Click the <LinkIcon sx={{ verticalAlign: 'middle', fontSize: 18 }} /> on the events you wish to add to this collection.
                                                    </Typography>
                                                    <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                                                        <Stack direction='column' spacing={0.75}>
                                                            {eventsNotInGroup.length === 0 ? (
                                                                <Typography variant='body2' color='text.secondary'>No unlinked events.</Typography>
                                                            ) : (
                                                                eventsNotInGroup.map((evt: any) => (
                                                                    <Button
                                                                        key={evt._id}
                                                                        variant='outlined'
                                                                        size='small'
                                                                        fullWidth
                                                                        onClick={() => linkEvent(evt)}
                                                                        sx={{ justifyContent: 'space-between', textTransform: 'none', py: 1 }}
                                                                    >
                                                                        <Typography variant='body2' noWrap sx={{ flex: 1, textAlign: 'left', pr: 1 }}>{evt.eventName ?? 'Untitled'}</Typography>
                                                                        <LinkIcon fontSize='small' sx={{ flexShrink: 0 }} />
                                                                    </Button>
                                                                ))
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Paper>
                                            </ClickAwayListener>
                                        </Popper>
                                        <Box ref={addEventButtonRef}>
                                            <StyledIconButton
                                                appTheme={appTheme}
                                                factor={factor}
                                                color='primary'
                                                icon={<AddIcon />}
                                                label='Add Event'
                                                onClick={() => setAddEventOpen(true)}
                                            />
                                        </Box>
                                        {showCleanseButton && (
                                            <Tooltip title='Remove event IDs that no longer match linked events'>
                                                <Button
                                                    variant='outlined'
                                                    size='small'
                                                    startIcon={<CleaningServicesIcon sx={{ fontSize: 18 }} />}
                                                    onClick={handleCleanseEventIDs}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: 12 * factor,
                                                        borderColor: 'sepia.main',
                                                        color: 'sepia.main',
                                                        '&:hover': { borderColor: 'sepia.dark', color: 'sepia.dark', backgroundColor: 'sepia.extraLight' },
                                                    }}
                                                >
                                                    Cleanse Event IDs
                                                </Button>
                                            </Tooltip>
                                        )}
                                        <Stack direction={'row'} spacing={1} alignItems='center'>
                                            <Typography variant='subtitle2'>Cards</Typography>
                                            <IOSSwitch
                                                size='small'
                                                checked={eventsViewMode === 'list'}
                                                onChange={(e) => setEventsViewMode(e.target.checked ? 'list' : 'cards')}
                                            />
                                            <Typography variant='subtitle2'>List</Typography>
                                        </Stack>
                                    </Stack>
                                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', mt: 1 }}>
                                        {linkedEvents.length === 0 ? (
                                            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                                                No events linked to this collection yet. Link an existing event or add a new one.
                                            </Typography>
                                        ) : eventsViewMode === 'cards' ? (
                                            <Stack
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: `repeat(auto-fill, ${400 * factor}px)`,
                                                    gap: 3,
                                                    justifyItems: 'start',
                                                    p: 1,
                                                    pb: 3,
                                                }}
                                            >
                                                {linkedEvents
                                                    .sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                                                    .map((event: any, index: number) => (
                                                    <CollectionEventCard
                                                        key={String(event._id ?? `col-evt-${index}`)}
                                                        event={event}
                                                        factor={factor}
                                                        onOpen={() => openEvent(event, { preserveCollection: true })}
                                                        onUnlink={(e) => unlinkEvent(event, e)}
                                                        onCopy={(e) => { e.stopPropagation(); handleOpenCopyDialog(event); }}
                                                    />
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Stack direction='column' spacing={0}>
                                                <Stack direction='row' spacing={2} alignItems='center'
                                                    sx={{
                                                        borderBottom: `1px solid ${theme.palette.primary.main}`,
                                                        padding: '8px 15px',
                                                        borderRadius: 1,
                                                        position: 'sticky',
                                                        top: 0,
                                                        zIndex: 1,
                                                        backgroundColor: 'sepia.background',
                                                    }}>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.eventName }}>Event</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.course }}>Course</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.dates }}>Dates</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.rounds }}>Rounds</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.holes }}>Holes</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.field }}>Field</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.playType }}>Play Type</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.privacy }}>Privacy</Typography>
                                                    <Typography variant='subtitle2' sx={{ fontWeight: 600, ...eventListCol.status }}>Status</Typography>
                                                    <Box sx={{ width: 72, minWidth: 72, flexShrink: 0 }} />
                                                </Stack>
                                                {linkedEvents
                                                    .sort((a: any, b: any) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                                                    .map((event: any, index: number) => (
                                                    <CollectionEventRow
                                                        key={String(event._id ?? `col-evt-${index}`)}
                                                        event={event}
                                                        appTheme={appTheme}
                                                        onOpen={() => openEvent(event, { preserveCollection: true })}
                                                        onUnlink={(e) => unlinkEvent(event, e)}
                                                        onCopy={(e) => { e.stopPropagation(); handleOpenCopyDialog(event); }}
                                                    />
                                                ))}
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>
                                <CopyEventDialog
                                    open={copyDialogOpen}
                                    onClose={() => { setCopyDialogOpen(false); setCopySourceEvent(null); }}
                                    sourceEvent={copySourceEvent}
                                    eventGroupID={viewGroup._id}
                                    onCreated={handleCopyEventCreated}
                                />
                                <NewTournament
                                    appDims={appDims}
                                    queryEventData={false}
                                    setQueryEventData={() => {}}
                                    eventGroupID={viewGroup._id}
                                    onEventCreated={handleNewEventCreated}
                                    controlledOpen={addEventOpen}
                                    onControlledClose={() => setAddEventOpen(false)}
                                    anchorEl={addEventButtonRef.current}
                                />
                            </TabPanel>

                            {/* Tab 1 — Players */}
                            <TabPanel value={groupTabValue} index={1} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
                                <Stack direction={'column'} spacing={1} sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
                                    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ borderBottom: `1px dashed ${theme.palette.divider}`, pb: 0.5, flexShrink: 0 }}>
                                        <Typography variant='subtitle1' color='primary' sx={{ fontWeight: 600 }}>
                                            Players
                                        </Typography>
                                        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
                                            <Typography variant='caption' color='text.secondary'>
                                                {playerRoster.length} player{playerRoster.length !== 1 ? 's' : ''}
                                            </Typography>
                                            {rosterMetrics.duplicateNames > 0 && (
                                                <Chip
                                                    size='small'
                                                    label={`${rosterMetrics.duplicateNames} duplicate name${rosterMetrics.duplicateNames !== 1 ? 's' : ''}`}
                                                    color='warning'
                                                    variant='outlined'
                                                    sx={{ fontSize: 11 * factor, height: 22 }}
                                                />
                                            )}
                                            {rosterMetrics.duplicateEmails > 0 && (
                                                <Chip
                                                    size='small'
                                                    label={`${rosterMetrics.duplicateEmails} duplicate email${rosterMetrics.duplicateEmails !== 1 ? 's' : ''}`}
                                                    color='warning'
                                                    variant='outlined'
                                                    sx={{ fontSize: 11 * factor, height: 22 }}
                                                />
                                            )}
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                                onClick={() => setAddPlayerDialogOpen(true)}
                                                sx={{ textTransform: 'none', fontSize: 12 * factor }}
                                            >
                                                Add Player
                                            </Button>
                                            {allowSaveRoster && (
                                                <Button
                                                    size='small'
                                                    variant='contained'
                                                    onClick={handleSaveRoster}
                                                    disabled={saving}
                                                    sx={{ textTransform: 'none', fontSize: 12 * factor }}
                                                >
                                                    Save Roster
                                                </Button>
                                            )}
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                startIcon={<ContentCopy sx={{ fontSize: 16 }} />}
                                                onClick={handleCopyEmails}
                                                disabled={playerRoster.length === 0}
                                                sx={{ textTransform: 'none', fontSize: 12 * factor }}
                                            >
                                                Copy Emails
                                            </Button>
                                            <Button
                                                size='small'
                                                variant='outlined'
                                                startIcon={syncingRoster ? <CircularProgress size={14} /> : <SyncIcon sx={{ fontSize: 16 }} />}
                                                onClick={handleSyncRoster}
                                                disabled={syncingRoster || !viewGroup.eventIDs?.length}
                                                sx={{ textTransform: 'none', fontSize: 12 * factor }}
                                            >
                                                Sync Roster
                                            </Button>
                                            <Tooltip title={playerRoster.length > 0 && playerRoster.every(p => p.locked) ? 'Unlock all rows' : 'Lock all rows'}>
                                                <Button
                                                    size='small'
                                                    variant='outlined'
                                                    startIcon={
                                                        playerRoster.length > 0 && playerRoster.every(p => p.locked)
                                                            ? <LockOpenIcon sx={{ fontSize: 16 }} />
                                                            : <LockIcon sx={{ fontSize: 16 }} />
                                                    }
                                                    onClick={handleLockUnlockAll}
                                                    disabled={playerRoster.length === 0}
                                                    sx={{ textTransform: 'none', fontSize: 12 * factor }}
                                                >
                                                    All
                                                </Button>
                                            </Tooltip>

                                            {/* Manage Columns button + Popper */}
                                            <Tooltip title='Manage roster columns'>
                                                <Button
                                                    ref={columnsPopperAnchorRef}
                                                    size='small'
                                                    variant='outlined'
                                                    startIcon={<ViewColumnIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => setColumnsPopperOpen(p => !p)}
                                                    sx={{ textTransform: 'none', fontSize: 12 * factor }}
                                                >
                                                    Columns
                                                </Button>
                                            </Tooltip>
                                            <Popper
                                                open={columnsPopperOpen}
                                                anchorEl={columnsPopperAnchorRef.current}
                                                placement='bottom-end'
                                                disablePortal={false}
                                                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                                                sx={{ zIndex: theme.zIndex.tooltip }}
                                            >
                                                <ClickAwayListener onClickAway={() => setColumnsPopperOpen(false)}>
                                                    <Paper elevation={4} sx={{ p: 2, minWidth: 260, maxHeight: 420, overflowY: 'auto' }}>
                                                        <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 0.5 }}>
                                                            Roster Columns
                                                        </Typography>
                                                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                                                            Select which data columns appear in the player roster.
                                                        </Typography>
                                                        <FormGroup>
                                                            {rosterFields.map(field => {
                                                                const isLocked = field.key === 'playerName' || field.key === 'playerEmail';
                                                                const isChecked = isLocked || rosterColumns.includes(field.key);
                                                                return (
                                                                    <Tooltip
                                                                        key={field.key}
                                                                        title={
                                                                            isLocked
                                                                                ? 'This column is always visible'
                                                                                : field.teeSetMode === 'smart'
                                                                                    ? 'Used for smart tee set suggestions'
                                                                                    : ''
                                                                        }
                                                                        placement='right'
                                                                    >
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    size='small'
                                                                                    checked={isChecked}
                                                                                    disabled={isLocked}
                                                                                    onChange={(e) => handleToggleRosterColumn(field.key, e.target.checked)}
                                                                                />
                                                                            }
                                                                            label={
                                                                                <Stack direction='row' spacing={0.5} alignItems='center'>
                                                                                    <Typography variant='body2'>{field.label}</Typography>
                                                                                    {field.teeSetMode === 'smart' && (
                                                                                        <Chip label='smart tee' size='small' color='info' variant='outlined' sx={{ fontSize: 10, height: 18, px: 0.5 }} />
                                                                                    )}
                                                                                </Stack>
                                                                            }
                                                                            sx={{ mx: 0, my: 0.25 }}
                                                                        />
                                                                    </Tooltip>
                                                                );
                                                            })}
                                                        </FormGroup>
                                                    </Paper>
                                                </ClickAwayListener>
                                            </Popper>
                                        </Stack>
                                    </Stack>
                                    <Box sx={{ flex: 1, minHeight: 0, width: '100%', overscrollBehavior: 'none', backgroundColor: 'sepia.background' }}>
                                        <DataGridPro
                                            rows={playerRoster}
                                            getRowId={(row) => row.index}
                                            columns={dynamicRosterColumns}
                                            editMode='row'
                                            processRowUpdate={(newRow, oldRow) => {
                                                const idx = playerRoster.findIndex((r) => r.index === (oldRow as PlayerRosterEntry).index);
                                                if (idx === -1) return oldRow;
                                                const updated = [...playerRoster];
                                                updated[idx] = {
                                                    ...playerRoster[idx],
                                                    ...newRow,
                                                    index: playerRoster[idx].index,
                                                    userID: playerRoster[idx].userID,
                                                    locked: playerRoster[idx].locked,
                                                };
                                                setPlayerRoster(updated);
                                                return updated[idx];
                                            }}
                                            density='compact'
                                            pinnedColumns={{ left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'playerName'], right: ['locked', '__delete'] }}
                                            checkboxSelection
                                            disableRowSelectionOnClick
                                            sx={{
                                                fontSize: 12 * factor,
                                                height: '100%',
                                                width: '100%',
                                                border: 'none',
                                                backgroundColor: 'sepia.background',
                                                '& .MuiDataGrid-main': {
                                                    backgroundColor: 'sepia.background',
                                                },
                                                '& .MuiDataGrid-virtualScroller': {
                                                    overscrollBehavior: 'none',
                                                    backgroundColor: 'sepia.background',
                                                },
                                                '& .MuiDataGrid-toolbarContainer': {
                                                    backgroundColor: 'sepia.background',
                                                },
                                                '& .MuiDataGrid-columnHeader': {
                                                    backgroundColor: 'sepia.background',
                                                },
                                                '& .MuiDataGrid-columnHeaderTitle': {
                                                    fontWeight: 500,
                                                    fontSize: 12 * factor,
                                                },
                                                '& .MuiDataGrid-iconButtonContainer': {
                                                    backgroundColor: 'transparent',
                                                    padding: 0,
                                                    margin: 0,
                                                    width: 15,
                                                    display: 'none',
                                                },
                                                '& .MuiDataGrid-sortIcon': {
                                                    width: 15,
                                                    height: 15,
                                                    color: 'red',
                                                    display: 'none',
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    backgroundColor: 'sepia.background',
                                                    '&:hover': { backgroundColor: 'rgba(235, 235, 235, 1)' },
                                                },
                                                '& .MuiDataGrid-row': {
                                                    backgroundColor: 'sepia.background',
                                                    '&:hover': { backgroundColor: 'rgba(235, 235, 235, 1)' },
                                                },
                                                '& .MuiDataGrid-row.Mui-selected': {
                                                    backgroundColor: 'rgba(235, 235, 235, 1)',
                                                    '&:hover': { backgroundColor: 'rgba(235, 235, 235, 1)' },
                                                },
                                            }}
                                        />
                                    </Box>
                                </Stack>
                                <AddPlayerDialog
                                    open={addPlayerDialogOpen}
                                    onClose={() => setAddPlayerDialogOpen(false)}
                                    onAdd={handleAddPlayer}
                                    appTheme={appTheme}
                                    factor={factor}
                                    templateKeys={rosterColumns}
                                    teeSetOptions={teeSetOpts}
                                    divisionOptions={divisionOpts}
                                    flightOptions={flightOpts}
                                />
                            </TabPanel>

                            {/* Tab 2 — Series Points */}
                            <TabPanel value={groupTabValue} index={2} appDims={appDims} compHeights={compHeights} factor={factor} heightOffset={110}>
                                <Box sx={{ height: '100%', overflow: 'hidden' }}>
                                    <SeriesPointsEditor divisionOpts={divisionAllOpts} />
                                </Box>
                            </TabPanel>

                            {/* Tab 3 — Standings */}
                            <TabPanel value={groupTabValue} index={3} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
                                <Stack direction='column' sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                    {/* Header row */}
                                    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ pb: 1, borderBottom: `1px dashed ${theme.palette.divider}`, flexShrink: 0 }}>
                                        <Typography variant='subtitle1' color='primary' sx={{ fontWeight: 600, fontSize: 14 * factor }}>
                                            Standings
                                        </Typography>
                                        
                                        <Stack direction='row' alignItems='center' spacing={4}>
                                            {/* Embed URL */}
                                            {viewGroup?._id && (
                                                <Stack direction='row' alignItems='center' sx={{ pt: 1, pb: 0.5, flexShrink: 0 }}>
                                                    <TextField
                                                        value={`${process.env.REACT_APP_MOMENT_CENTRAL_URL ?? ''}standings?seriesID=${viewGroup._id}`}
                                                        size='small'
                                                        label='Embed URL'
                                                        inputProps={{ readOnly: true, style: { fontSize: 11 * factor, cursor: 'text' } }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position='end'>
                                                                    <Tooltip title={copiedEmbedUrl ? 'Copied!' : 'Copy embed URL'}>
                                                                        <IconButton
                                                                            size='small'
                                                                            edge='end'
                                                                            onClick={() => {
                                                                                const url = `${process.env.REACT_APP_MOMENT_CENTRAL_URL ?? ''}standings?seriesID=${viewGroup._id}`;
                                                                                navigator.clipboard.writeText(url).then(() => {
                                                                                    setCopiedEmbedUrl(true);
                                                                                    setTimeout(() => setCopiedEmbedUrl(false), 2000);
                                                                                });
                                                                            }}
                                                                        >
                                                                            {copiedEmbedUrl
                                                                                ? <CheckCircleOutlinedIcon sx={{ fontSize: 14 * factor, color: 'success.main' }} />
                                                                                : <ContentCopy sx={{ fontSize: 14 * factor }} />
                                                                            }
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': { backgroundColor: 'sepia.background', fontSize: 11 * factor },
                                                            '& .MuiInputLabel-root': { fontSize: 11 * factor },
                                                            width: 300,
                                                        }}
                                                    />
                                                </Stack>
                                            )}
                                            {(collectionStandings?.calculatedThrough ?? collectionStandings?.computedAt) && (
                                                <Stack spacing={0.1}>
                                                    <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 * factor }}>
                                                        Standings through:{' '}
                                                        <strong>
                                                            {new Date(collectionStandings!.calculatedThrough ?? collectionStandings!.computedAt!).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}
                                                        </strong>
                                                    </Typography>
                                                    <Typography variant='caption' color='text.disabled' sx={{ fontSize: 10 * factor }}>
                                                        Calculated at:{' '}
                                                        {new Date(collectionStandings!.calculatedAt ?? collectionStandings!.computedAt!).toLocaleString()}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            <Button
                                                variant='outlined'
                                                size='small'
                                                onClick={handlePreviewStandings}
                                                disabled={previewingStandings || computingStandings}
                                                startIcon={previewingStandings ? <CircularProgress size={12} /> : <VisibilityIcon sx={{ fontSize: 14 * factor }} />}
                                                sx={{ fontSize: 12 * factor, textTransform: 'none', height: 28 * factor }}
                                            >
                                                {previewingStandings ? 'Loading…' : 'Preview Standings'}
                                            </Button>
                                            <Button
                                                variant='outlined'
                                                size='small'
                                                onClick={handleComputeStandings}
                                                disabled={computingStandings || previewingStandings}
                                                startIcon={computingStandings ? <CircularProgress size={12} /> : <SyncIcon sx={{ fontSize: 14 * factor }} />}
                                                sx={{ fontSize: 12 * factor, textTransform: 'none', height: 28 * factor }}
                                            >
                                                {computingStandings ? 'Calculating…' : 'Calculate Standings'}
                                            </Button>
                                        </Stack>
                                    </Stack>

                                    

                                    {/* View toggle */}
                                    <Stack direction='row' alignItems='center' sx={{ pt: 1, pb: 0.5, flexShrink: 0 }}>
                                        <ToggleButtonGroup
                                            value={standingsView}
                                            exclusive
                                            onChange={(_, val) => { if (val) setStandingsView(val); }}
                                            size='small'
                                            sx={{ '& .MuiToggleButton-root': { fontSize: 12 * factor, textTransform: 'none', px: 1.5, py: 0.5 } }}
                                        >
                                            <ToggleButton value='individual'>Individual</ToggleButton>
                                            <ToggleButton value='pairs'>Pairs</ToggleButton>
                                            <ToggleButton value='teams'>Teams</ToggleButton>
                                        </ToggleButtonGroup>
                                    </Stack>

                                    {/* Standings grid */}
                                    <Box sx={{ flex: 1, minHeight: 0, minWidth: 400, maxWidth: 500 }}>
                                        {!collectionStandings ? (
                                            <Typography variant='body2' color='text.secondary' sx={{ fontSize: 12 * factor, pt: 2 }}>
                                                No standings computed yet. Click &quot;Calculate Standings&quot; to generate.
                                            </Typography>
                                        ) : standingsView === 'individual' ? (
                                            <DataGridPro
                                                rows={collectionStandings.individual.map((row, i) => ({ id: i, ...row }))}
                                                columns={[
                                                    { field: 'rank',         headerName: 'Rank',   width: 54 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: standingsWrapCell },
                                                    { field: 'playerName',   headerName: 'Player', flex: 1, minWidth: 80,              renderCell: standingsWrapCell },
                                                    { field: 'totalPoints',  headerName: 'Points', width: 80 * factor, type: 'number', align: 'right',  headerAlign: 'right',  renderCell: standingsWrapCell },
                                                    { field: 'eventsPlayed', headerName: 'Events', width: 64 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: standingsWrapCell },
                                                ] satisfies GridColDef[]}
                                                density='compact'
                                                getRowHeight={() => 'auto'}
                                                disableColumnMenu
                                                disableRowSelectionOnClick
                                                hideFooter={collectionStandings.individual.length <= 100}
                                                sx={{ ...standingsGridSx(factor), width: '100%', height: '100%' }}
                                            />
                                        ) : standingsView === 'pairs' ? (
                                            <DataGridPro
                                                rows={collectionStandings.pairs.map((row, i) => ({ id: i, ...row }))}
                                                columns={[
                                                    { field: 'rank',         headerName: 'Rank',   width: 54 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: standingsWrapCell },
                                                    { field: 'pairName',     headerName: 'Pair',   flex: 1, minWidth: 80,              renderCell: standingsWrapCell },
                                                    { field: 'totalPoints',  headerName: 'Points', width: 80 * factor, type: 'number', align: 'right',  headerAlign: 'right',  renderCell: standingsWrapCell },
                                                    { field: 'eventsPlayed', headerName: 'Events', width: 64 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: standingsWrapCell },
                                                ] satisfies GridColDef[]}
                                                density='compact'
                                                getRowHeight={() => 'auto'}
                                                disableColumnMenu
                                                disableRowSelectionOnClick
                                                hideFooter={collectionStandings.pairs.length <= 100}
                                                sx={{ ...standingsGridSx(factor), width: '100%', height: '100%' }}
                                            />
                                        ) : (
                                            <DataGridPro
                                                rows={collectionStandings.teams.map((row, i) => ({ id: i, ...row }))}
                                                columns={[
                                                    { field: 'rank',            headerName: 'Rank',   width: 54 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: standingsWrapCell },
                                                    { field: 'teamName',        headerName: 'Team',   flex: 1, minWidth: 80,              renderCell: standingsWrapCell },
                                                    { field: 'totalPoints',     headerName: 'Points', width: 80 * factor, type: 'number', align: 'right',  headerAlign: 'right',  renderCell: standingsWrapCell },
                                                    { field: 'eventsQualified', headerName: 'Events', width: 64 * factor, type: 'number', align: 'center', headerAlign: 'center', renderCell: standingsWrapCell },
                                                ] satisfies GridColDef[]}
                                                density='compact'
                                                getRowHeight={() => 'auto'}
                                                disableColumnMenu
                                                disableRowSelectionOnClick
                                                hideFooter={collectionStandings.teams.length <= 100}
                                                sx={{ ...standingsGridSx(factor), width: '100%', height: '100%' }}
                                            />
                                        )}
                                    </Box>
                                </Stack>
                            </TabPanel>

                            {/* Tab 4 — Collection Details */}
                            <TabPanel value={groupTabValue} index={4} appDims={appDims} compHeights={compHeights} factor={factor} fillAvailable>
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        height: '100%',
                                        minHeight: 0,
                                        flex: 1,
                                        boxSizing: 'border-box',
                                        p: 1.5,
                                        overflowX: 'hidden',
                                        overflowY: 'auto',
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(${detailsColumnCount}, minmax(0, 1fr))`,
                                        gap: 2.5,
                                        alignItems: 'start',
                                    }}
                                >
                                    {detailsColumns.map((columnCards, columnIndex) => (
                                        <Stack
                                            key={`collection-details-column-${columnIndex}`}
                                            direction="column"
                                            spacing={0}
                                            sx={{ minWidth: 0, maxWidth: '100%' }}
                                        >
                                            {columnCards}
                                        </Stack>
                                    ))}
                                </Box>
                            </TabPanel>

                        </Box>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>

        {/* Standings preview dialog — read-only, no DB write */}
        {previewData && (
            <StandingsPreviewDialog
                open={!!previewData}
                onClose={() => setPreviewData(null)}
                previewData={previewData}
                factor={factor}
            />
        )}

        {/* Calculate-through event picker */}
        <CalculateThroughDialog
            open={calculateThroughOpen}
            onClose={() => setCalculateThroughOpen(false)}
            onConfirm={handleConfirmCalculate}
            linkedEvents={(linkedEventObjs as any[]).map((e: any) => ({
                _id: e._id,
                eventName: e.eventName ?? '',
                startDate: e.startDate ?? '',
                endDate: e.endDate ?? '',
            }))}
            factor={factor}
        />
        </>
    );
};

export default GroupEditor;
